import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize the Google Generative AI with your API key
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || '');

interface ScriptManagerProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

export const ScriptManager: React.FC<ScriptManagerProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [script, setScript] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async () => {
    if (!script.trim()) {
      setError('Please paste a script');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call Gemini API to parse the script
      const model = genAI.getGenerativeModel({ model: 'gemini-1.5-pro' });
      
      const prompt = `**TASK:** Parse the provided script into an intro line, an array of individual song lines, and an outro line.

**CRITICAL INSTRUCTIONS:**
1.  **Preserve Exact Wording and Order:** You MUST keep the exact phrasing, word order, and punctuation of each line as it appears in the original script. Do NOT rephrase, reorder, summarize, or change any part of the text for the intro, each song line, or the outro. Your sole job is to identify the sections and return the text EXACTLY as provided within those sections.
2.  **Identify Sections:** Correctly identify the single introductory line, the sequence of lines describing each song (typically 10 lines), and the single concluding outro line.
3.  **Output Format:** Return ONLY a single, valid JSON object with the following structure:
    \`\`\`json
{
      "intro": "Exact intro text from script",
      "songs": [
        "Exact text for song 10 from script",
        "Exact text for song 9 from script",
        ...
        "Exact text for song 1 from script"
      ],
      "outro": "Exact outro text from script"
}
    \`\`\`
4.  **Handle Missing Sections:** If the script doesn't contain a clear intro or outro, return an empty string "" for that key. The "songs" array should contain exactly one string for each song mentioned, in the order they appear in the script.

**Script to Parse:**
${script}`;

      const result = await model.generateContent(prompt);
      const geminiResponse = await result.response;
      const text = geminiResponse.text();
      
      console.log('Raw Gemini Response:', text);
      
      try {
        // Extract JSON from markdown code block if present
        const jsonMatch = text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
        console.log('JSON Match:', jsonMatch);
        
        const jsonText = jsonMatch ? jsonMatch[1] : text;
        console.log('JSON Text to Parse:', jsonText);
        
        const parsedScript = JSON.parse(jsonText);
        console.log('Parsed Script:', parsedScript);
        
        // Validate the parsed script structure (allow empty intro/outro strings)
        if (typeof parsedScript.intro !== 'string' || !Array.isArray(parsedScript.songs) || typeof parsedScript.outro !== 'string') {
          console.error('Invalid script structure:', parsedScript);
          throw new Error('Invalid script structure: Missing required keys or incorrect types.');
        }
        
        onSave(parsedScript);
        onClose();
      } catch (err) {
        setError('Failed to parse Gemini response');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse script');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload Script</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Paste your full script below. The script will be parsed into an intro, individual song lines, and an outro.
          </Typography>
          <TextField
            multiline
            rows={10}
            fullWidth
            value={script}
            onChange={(e) => setScript(e.target.value)}
            placeholder="Paste your script here..."
            sx={{ mt: 2 }}
          />
          {error && (
            <Typography color="error" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export {}; 